import { cn } from '@/lib/utils'

/**
 * Codes → words for the ballot.
 *
 * Same rule as every other `vocab` file here: this maps a code the server sent, and can
 * never ADD an option. Seat types and nomination statuses both come from served vocabulary
 * arrays; a hand-written `<option>` list that drifts is the defect `VocabularyContractTest`
 * exists to prevent.
 */

const LABELS: Record<string, string> = {
  // ── seats. The level matters as much as the name — see SEAT_LEVEL below. ──
  PRESIDENT: 'President',
  GOVERNOR: 'Governor',
  SENATOR: 'Senator',
  WOMAN_REP: 'Woman Representative',
  MP: 'Member of Parliament',
  MCA: 'Member of County Assembly',

  // ── the nomination ladder ───────────────────────────────────────────────
  PROSPECTIVE: 'Prospective',
  NOMINATED: 'Nominated',
  CLEARED: 'Cleared for the ballot',
  CERTIFICATE_ISSUED: 'Certificate issued',
  WITHDRAWN: 'Withdrawn',
  DISQUALIFIED: 'Disqualified',

  // ── provenance ──────────────────────────────────────────────────────────
  MANUAL: 'Entered by hand',
  IMPORT: 'Imported from a file',
  OCR: 'Read from a scanned form',

  // ── geography levels, as the seat rule names them ───────────────────────
  COUNTRY: 'National',
  COUNTY: 'County',
  CONSTITUENCY: 'Constituency',
  WARD: 'Ward',
  POLLING_STATION: 'Polling station',
}

export function label(code?: string | null): string {
  if (!code) return '—'
  return LABELS[code] ?? code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ')
}

export function options(codes: string[] | null | undefined) {
  return (codes ?? []).map((c) => ({ value: c, label: label(c) }))
}

/**
 * The six seats, in the order a ballot paper stacks them.
 *
 * Not alphabetical: this is the order Kenyan voters meet them at a general election, and a
 * seat list sorted A–Z reads as a list of unrelated offices rather than one ballot.
 */
export const SEAT_ORDER = ['PRESIDENT', 'GOVERNOR', 'SENATOR', 'WOMAN_REP', 'MP', 'MCA']

/**
 * The geography level each seat is contested at.
 *
 * A CLIENT-SIDE MIRROR of the server's `SeatTypeRules`, used only to filter a unit picker
 * to the right level before the user submits. **The server's copy is the authority** — it
 * refuses a mismatch with a problem type carrying `requiredLevel`, and the response's own
 * `requiredLevel` field is preferred wherever a row has been loaded. This exists so the
 * picker is right the first time, not so the client can decide.
 */
export const SEAT_LEVEL: Record<string, string> = {
  PRESIDENT: 'COUNTRY',
  GOVERNOR: 'COUNTY',
  SENATOR: 'COUNTY',
  WOMAN_REP: 'COUNTY',
  MP: 'CONSTITUENCY',
  MCA: 'WARD',
}

/** Where a candidacy sits on the ladder. Colour never travels without the word. */
export function NominationChip({ code }: { code?: string | null }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-erp-xs font-medium whitespace-nowrap',
      tone(code),
    )}>
      {label(code)}
    </span>
  )
}

function tone(code?: string | null): string {
  switch (code) {
    case 'PROSPECTIVE': return 'border-border bg-muted/50 text-muted-foreground'
    case 'NOMINATED': return 'border-sky-300 bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300'
    case 'CLEARED': return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
    // The one that means "on the paper". Given the strongest treatment on purpose.
    case 'CERTIFICATE_ISSUED': return 'border-emerald-500 bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200'
    case 'WITHDRAWN': return 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
    case 'DISQUALIFIED': return 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
    default: return 'border-border bg-muted/40'
  }
}

/**
 * A party, with its own colour as a left edge.
 *
 * The colour is the party's registered one, so it is identity and not a scale — it is used
 * as a 3px edge rather than a fill, because a full-bleed party colour behind text has no
 * contrast guarantee at all and the name is what must stay readable.
 */
export function PartyChip({
  name, shortName, colourHex, independent,
}: {
  name?: string | null
  shortName?: string | null
  colourHex?: string | null
  independent?: boolean
}) {
  if (independent) {
    return (
      <span className="inline-flex items-center rounded border border-dashed border-border px-1.5 py-0.5 text-erp-xs text-muted-foreground">
        Independent
      </span>
    )
  }
  if (!name && !shortName) return <span className="text-muted-foreground">—</span>
  return (
    <span
      className="inline-flex items-center rounded border border-border bg-muted/40 px-1.5 py-0.5 text-erp-xs font-medium"
      style={colourHex ? { borderLeftWidth: 3, borderLeftColor: colourHex } : undefined}
      title={name ?? undefined}
    >
      {shortName ?? name}
    </span>
  )
}
